import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Outlet, NavLink, Link } from "react-router-dom";
import { api, LOGO_URL } from "../lib/config";
import { useAuth } from "../lib/auth";
import { LayoutDashboard, Image as ImageIcon, Star, HelpCircle, MapPin, FileText, Users, Award, Grid, BookOpen, MessageSquare, Settings, LogOut, ShieldAlert, Network, Layers, Briefcase, Download } from "lucide-react";
import { toast } from "sonner";

export const AuthCallback = () => {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) { navigate("/admin/login"); return; }
    const sid = match[1];
    (async () => {
      try {
        const r = await api.post("/auth/session", { session_id: sid });
        setUser(r.data.user);
        window.history.replaceState(null, "", "/admin");
        navigate("/admin", { replace: true });
      } catch {
        toast.error("Connexion échouée");
        navigate("/admin/login");
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen bg-[#171717] flex items-center justify-center">
      <p className="text-[#DDD4B2]/70">Connexion en cours…</p>
    </div>
  );
};

export const AdminLogin = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user?.is_admin) navigate("/admin", { replace: true });
  }, [user, loading, navigate]);

  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#171717] flex items-center justify-center p-6" data-testid="admin-login-page">
      <div className="max-w-md w-full">
        <Link to="/" className="flex items-center gap-3 justify-center mb-10">
          <img src={LOGO_URL} alt="Passion Verre" className="w-16 h-16 object-contain" />
        </Link>
        <div className="border border-[#9A8640]/30 p-10 text-center">
          <h1 className="font-serif text-3xl text-white mb-2">Espace administrateur</h1>
          <p className="text-sm text-[#DDD4B2]/70 mb-8">Connectez-vous avec votre compte Google pour gérer le contenu du site.</p>
          <button onClick={signIn} className="btn-gold px-8 py-4 text-sm uppercase tracking-[0.18em] w-full" data-testid="admin-google-signin">
            Se connecter avec Google
          </button>
          <p className="text-xs text-[#DDD4B2]/50 mt-6">
            {user && !user.is_admin && (
              <span className="text-red-400">Votre compte n'a pas les droits administrateur.</span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

const MENU = [
  { to: "/admin", label: "Tableau de bord", icon: LayoutDashboard, end: true },
  { to: "/admin/content", label: "Contenu du site", icon: Settings },
  { to: "/admin/universes", label: "Univers (Accueil)", icon: Grid },
  { to: "/admin/client-logos", label: "Logos clients", icon: Users },
  { to: "/admin/best-sellers", label: "Best-sellers pros", icon: Award },
  { to: "/admin/pro-realizations", label: "Réalisations pros", icon: Briefcase },
  { to: "/admin/gallery", label: "Galerie réalisations", icon: ImageIcon },
  { to: "/admin/gallery-categories", label: "Catégories galerie", icon: Layers },
  { to: "/admin/blog", label: "Blog", icon: BookOpen },
  { to: "/admin/reviews", label: "Avis clients", icon: Star },
  { to: "/admin/faqs", label: "FAQ", icon: HelpCircle },
  { to: "/admin/markers", label: "Carte / Graveurs", icon: MapPin },
  { to: "/admin/legal", label: "Pages légales", icon: FileText },
  { to: "/admin/messages", label: "Messages reçus", icon: MessageSquare, badgeKey: "messages" },
  { to: "/admin/franchise", label: "Candidatures franchise", icon: Network, badgeKey: "franchise" },
  { to: "/admin/export-import", label: "Migration des données", icon: Download },
];

export const AdminLayout = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [counts, setCounts] = useState({ messages: 0, franchise: 0 });

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) navigate("/admin/login", { replace: true });
  }, [user, loading, navigate]);

  // Fetch unread counts on mount and every 60s, and on route change (so badge decreases
  // after admin reads/marks messages).
  useEffect(() => {
    if (!user?.is_admin) return;
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get("/admin/unread-counts");
        if (alive) setCounts(r.data || { messages: 0, franchise: 0 });
      } catch {}
    };
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [user?.is_admin, location.pathname]);

  if (loading || !user) return <div className="min-h-screen bg-white flex items-center justify-center text-gray-500">Chargement…</div>;
  if (!user.is_admin) return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6">
      <div className="border border-gray-200 bg-white shadow-sm p-10 text-center max-w-md rounded-md">
        <ShieldAlert className="mx-auto text-[#9A8640] mb-4" size={32} />
        <h1 className="font-serif text-2xl text-gray-900 mb-2">Accès refusé</h1>
        <p className="text-sm text-gray-600">Votre compte n'a pas les droits administrateur.</p>
      </div>
    </div>
  );

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  return (
    <div className="admin-light min-h-screen flex" data-testid="admin-layout">
      <aside className="w-72 border-r border-[#9A8640]/20 p-6 flex flex-col">
        <Link to="/admin" className="flex items-center gap-3 mb-10">
          <img src={LOGO_URL} alt="" className="w-10 h-10 object-contain" />
          <div>
            <div className="font-serif text-lg text-[#DDD4B2]">Admin</div>
            <div className="text-[10px] uppercase tracking-widest text-[#9A8640]">Passion Verre</div>
          </div>
        </Link>
        <nav className="flex-1 space-y-1 overflow-y-auto" data-testid="admin-sidebar">
          {MENU.map((m) => {
            const count = m.badgeKey ? counts[m.badgeKey] : 0;
            return (
              <NavLink
                key={m.to}
                to={m.to}
                end={m.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 text-sm rounded-sm transition-all ${
                    isActive ? "bg-[#9A8640] text-[#171717]" : "text-[#DDD4B2]/80 hover:bg-[#9A8640]/10 hover:text-[#DDD4B2]"
                  }`
                }
                data-testid={`admin-nav-${m.to.replace(/\//g, "-")}`}
              >
                <m.icon size={16} />
                <span>{m.label}</span>
                {count > 0 && (
                  <span className="admin-badge" data-testid={`admin-badge-${m.badgeKey}`}>{count > 99 ? "99+" : count}</span>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className="mt-6 pt-6 border-t border-[#9A8640]/20">
          <div className="flex items-center gap-3 px-2 mb-4">
            {user.picture ? <img src={user.picture} alt="" className="w-8 h-8 rounded-full" /> : <div className="w-8 h-8 rounded-full bg-[#9A8640]/20" />}
            <div className="text-xs flex-1 truncate">
              <div className="text-[#DDD4B2] truncate">{user.name}</div>
              <div className="text-[#DDD4B2]/50 truncate">{user.email}</div>
            </div>
          </div>
          <div className="flex gap-2">
            <Link to="/" className="flex-1 text-center text-xs px-3 py-2 border border-[#9A8640]/40 hover:bg-[#9A8640]/10">Site</Link>
            <button onClick={doLogout} className="flex-1 text-xs px-3 py-2 border border-[#9A8640]/40 hover:bg-red-500/20 inline-flex items-center justify-center gap-2" data-testid="admin-logout">
              <LogOut size={12} /> Sortir
            </button>
          </div>
        </div>
      </aside>
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
